<template>
  <transition name="longerFade">
    <div class="introduction" v-if="showComponent">
      <canvas ref="canvas"></canvas>
      <transition
        name="fade"
        v-on:after-enter="afterWhiteFade"
        v-on:enter="whiteFade"
      >
        <div class="white" v-show="fadeToWhite"></div>
      </transition>
    </div>
  </transition>
</template>

<script>
  import { mapMutations } from 'vuex';
  import { cover } from 'intrinsic-scale';
  import { CanvasTextWrapper } from 'canvas-text-wrapper';
  import glitch from '@/../lib/glitch-canvas-browser-with-polyfills.min';
  import WebFont from 'webfontloader';

  const logoImageSource = require('@/../static/images/graphics/logo.svg');

  const backgrounds = [
    require('@/../static/images/backgrounds/palm-trees.jpg'), //eslint-disable-line
    require('@/../static/images/backgrounds/palm-trees@2x.jpg'), //eslint-disable-line
    require('@/../static/images/backgrounds/palm-trees@3x.jpg'), //eslint-disable-line
  ];

  export default {
    name: 'introduction',
    data() {
      return {
        msg: 'In 1946, the US Navy conducted ‘Operation Crossroads’; a series of nuclear tests at Bikini',
        currentChar: 0,
        msgTyped: '',
        context: undefined,
        backgroundImage: undefined,
        logoImage: undefined,
        cacheCanvas: undefined,
        cacheContext: undefined,
        raf: 0,
        textWidth: 0,
        fadeToWhite: false,
        showComponent: true,
        glitchDone: false,
      };
    },
    mounted() {
      this.cacheCanvas = document.createElement('canvas');
      this.cacheContext = this.cacheCanvas.getContext('2d');

      const canvas = this.$refs.canvas;
      const context = canvas.getContext('2d');
      this.canvas = canvas;
      this.context = context;

      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerWidth * window.devicePixelRatio;

      this.cacheCanvas.width = canvas.width / 8;
      this.cacheCanvas.height = canvas.height / 8;

      const bgimage = new Image();
      let imageSource = '/static/images/backgrounds/';

      let dpr = Math.round(window.devicePixelRatio - 1);
      if (isNaN(dpr)) {
        dpr = 0;
      } else if (dpr > 2) {
        dpr = 2;
      }

      imageSource = backgrounds[dpr];

      const bgImagePromise = new Promise((resolve) => {
        bgimage.onload = () => {
          resolve(bgimage);
        };
      });

      const logoImage = new Image();

      const logoImagePromise = new Promise((resolve) => {
        logoImage.onload = () => {
          resolve(logoImage);
        };
      });

      const webFontPromise = new Promise((resolve) => {
        WebFont.load({ // eslint-disable-line
          google: {
            families: [
              'Open Sans',
              'Special Elite',
            ],
          },
          active: resolve,
        });
      });

      Promise.all([bgImagePromise, logoImagePromise, webFontPromise]).then((datas) => {
        this.backgroundImage = datas[0];
        this.logoImage = datas[1];
        this.resize(canvas, context);
        this.draw().then(this.typeOut());
      });

      bgimage.src = imageSource;
      logoImage.src = logoImageSource;

      window.addEventListener('resize', this.resize.bind(this));
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.resize.bind(this));
      this.setExplosion({ play: false });
    },
    methods: {
      ...mapMutations('ui', [
        'setShouldTypeStoryText',
        'setExplosion',
      ]),
      typeOut() {
        if (this.currentChar >= this.msg.length) {
          setTimeout(() => {
            cancelAnimationFrame(this.raf);
            this.raf = requestAnimationFrame(this.loopGlitch);
            setTimeout(() => {
              this.fadeToWhite = true;
            }, 225);
          }, 130);
          return;
        }
        this.msgTyped += this.msg[this.currentChar];
        this.currentChar += 1;

        setTimeout(() => {
          this.raf = requestAnimationFrame(this.typeOut);
        }, (Math.random() * 55) + 45);

        this.draw();
        if (
          (this.currentChar > 9 && this.currentChar < 15) ||
          (this.currentChar > 40 && this.currentChar < 42) ||
          (this.currentChar > 45 && this.currentChar < 49) ||
          (this.currentChar > 60 && this.currentChar <= 61)
        ) {
          this.drawGlitch();
        }
      },
      loopGlitch(delta) {
        if (Math.round(delta) % 2 === 0) {
          this.drawGlitch().then(() => {
            if (!this.glitchDone) this.raf = requestAnimationFrame(this.loopGlitch);
          });
        } else {
          if (!this.glitchDone) { //eslint-disable-line
            this.raf = requestAnimationFrame(this.loopGlitch);
          }
        }
      },
      drawGlitch() {
        return new Promise((resolve) => {
          const { canvas, context, cacheCanvas, cacheContext } = this;
          cacheContext.drawImage(canvas, 0, 0, cacheCanvas.width, cacheCanvas.height);

          glitch({ seed: Math.floor(Math.random() * 20) + 10, quality: 99, amount: 0 })
            .fromImageData(cacheContext.getImageData(0, 0, cacheCanvas.width, cacheCanvas.height))
            .toImageData()
            .then((glitchedImgData) => {
              cacheContext.putImageData(
                glitchedImgData,
                0, 0,
              );
              context.drawImage(cacheCanvas, 0, 0, canvas.width, canvas.height);
              resolve();
            })
            .catch(() => {
              resolve();
            });
        });
      },
      draw() {
        return new Promise((resolve) => {
          const { canvas, context, backgroundImage, logoImage } = this;

          const { width, height, x, y } = cover(
            canvas.width,
            canvas.height,
            backgroundImage.width,
            backgroundImage.height,
          );

          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(backgroundImage, x, y, width, height);

          const logoAspectRatio = 2.567307692;

          const logoHeight = (canvas.height / 100) * 19.25;
          const logoWidth = logoHeight * logoAspectRatio;

          context.drawImage(
            logoImage,
            (canvas.width / 2) - (logoWidth / 2),
            (canvas.height / 100) * 7.04,
            logoWidth,
            logoHeight,
          );

          context.fillStyle = '#fff';

          this.textSize = 28;
          const actualWidth = (canvas.width / window.devicePixelRatio);

          if (actualWidth < 768) {
            this.textSize = 20;
          }

          if (actualWidth < 400) {
            this.textSize = 18;
          }

          if (actualWidth < 375) {
            this.textSize = 18;
          }

          CanvasTextWrapper(canvas, this.msgTyped, {
            renderHDPI: false,
            font: `${this.textSize * window.devicePixelRatio}px "Special Elite", cursive`,
            textAlign: 'center',
            paddingY: (canvas.height / 100) * 31.111111111,
            paddingX: this.textWidth,
            lineHeight: `${this.textSize * (window.devicePixelRatio + 0.8)}px`,
          });

          resolve();
        });
      },
      resize() {
        const { canvas } = this;

        canvas.width = window.innerWidth * window.devicePixelRatio;
        canvas.height = window.innerHeight * window.devicePixelRatio;

        this.cacheCanvas.width = canvas.width / 8;
        this.cacheCanvas.height = canvas.height / 8;

        this.context.fontStyle = `${this.textSize * window.devicePixelRatio}px "Special Elite", cursive`;
        this.textWidth = Math.abs(
          (this.canvas.width / 3) - (this.context.measureText(this.msg).width),
        );

        this.draw();
      },
      whiteFade() {
        this.setShouldTypeStoryText({ shouldType: true });
        this.setExplosion({ play: true });
      },
      afterWhiteFade() {
        this.glitchDone = true;
        this.showComponent = false;
        cancelAnimationFrame(this.raf);
      },
    },
  };
</script>

<style scoped>
  .introduction, canvas {
    background: #3272D8;
    background: linear-gradient(to bottom, #3272d8 0%,#f9f2ec 100%);
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .white {
    background-color: #fff;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .4s ease-out;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .longerFade-enter-active, .longerFade-leave-active {
    transition: opacity 5s ease-in;
  }
  .longerFade-enter, .longerFade-leave-to {
    opacity: 0;
    pointer-events: none;
  }
</style>


// WEBPACK FOOTER //
// Introduction.vue?0372c462